import React, { useState, useEffect, useCallback } from 'react';
import { inquiriesService } from '../../services/inquiriesService';
import { contactService } from '../../services/contactService';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Inbox, Loader2, UserPlus } from 'lucide-react';

const InquiriesPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingId, setAddingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await inquiriesService.listInquiries();
      setData(res);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddToChat = async (contactId, flowDataId) => {
    if (!contactId) {
      toast.error('No contact linked to this inquiry yet');
      return;
    }
    try {
      setAddingId(flowDataId);
      await contactService.setChatActive(contactId, true);
      toast.success('Contact added to chat');
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Could not update contact');
    } finally {
      setAddingId(null);
    }
  };

  const handleGoToChat = (contactId) => {
    if (!contactId) {
      toast.error('No contact linked to this inquiry yet');
      return;
    }
    navigate('/contacts', { state: { contactId } });
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return String(iso);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const cols = data?.columnDefinitions || [];
  const rows = data?.rows || [];

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-primary-100 flex items-center justify-center">
          <Inbox className="h-6 w-6 text-primary-700" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Inquiries</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            WhatsApp Flow submissions, newest first
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 bg-white py-16 text-center text-gray-500">
          No flow inquiries yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">Updated</th>
                {cols.map((c) => (
                  <th key={c.key} className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">
                    {c.label || c.key}
                  </th>
                ))}
                <th className="px-3 py-3 text-left font-medium text-gray-700 whitespace-nowrap">Chat action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.flowDataId} className="hover:bg-gray-50/80">
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatTime(row.updatedAt)}</td>
                  {cols.map((c) => (
                    <td key={c.key} className="px-3 py-2 text-gray-800 max-w-xs truncate" title={row.values?.[c.key] ?? ''}>
                      {row.values?.[c.key] ?? ''}
                    </td>
                  ))}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      type="button"
                      disabled={!row.contactId || addingId === row.flowDataId}
                      onClick={() => (
                        row.isChatActive
                          ? handleGoToChat(row.contactId)
                          : handleAddToChat(row.contactId, row.flowDataId)
                      )}
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {addingId === row.flowDataId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <UserPlus className="h-3.5 w-3.5" />
                      )}
                      {row.isChatActive ? 'Go to chat' : 'Add to chat'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InquiriesPage;
